import {
  Box,
  Col,
  css,
  CSSProps,
  cx,
  DSButton,
  DSIconButton,
  DSIconEye,
  DSIconFlash,
  DSIconPencil,
  DSInput,
  DSSpinner,
  Row,
  Text,
} from "@cdx/ds";
import {VisionBoardNode, VisionBoardQueryPayload} from "../../../cdx-models/utils/extended-fields";
import {NodeBushEntry} from "../useVisionBoardData";
import {TwoNums, VisionBoardBag} from "../canvas-utils";
import {useCanvasNode} from "./useCanvasNode";
import {
  ArrowOverlay,
  CheckboxGroup,
  DropDownForChild,
  Label,
  RadioGroup,
  TooltipForChild,
  useEsc,
} from "@cdx/common";
import {
  Dispatch,
  KeyboardEvent,
  MouseEventHandler,
  Ref,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {canvasNodeStyles} from "../vision-board.css";
import CircularProgressArc from "../../../components/ProgressArc";
import {defaultRangeExtractor, Range, useVirtualizer, VirtualItem} from "@tanstack/react-virtual";
import {CARD_ROW_HEIGHT} from "../../card-panel/TableView";
import {makeScrollable} from "@cdx/ds/utils/makeScrollable";
import {getStatusForCard} from "../../../lib/card-status-utils";
import {checkIfPresent, useInstance} from "../../../lib/mate/mate-utils";
import {tableViewStyles} from "../../card-panel/card-panel.css";
import {cardStatusVariants} from "@cdx/ds/components/DSCard/DSCardTheme.css";
import {
  CheckListCell,
  DeckCell,
  EffortCell,
  MsCell,
  OwnerCell,
  preloadProjectTags,
  PrioCell,
  SprintCell,
  StatusRect,
  TitleCell,
} from "../../card-panel/TableViewCells";
import {Card, CardId} from "../../../cdx-models/Card";
import {Root} from "../../../cdx-models/Root";
import {api} from "../../../lib/api";
import {useCanvasWheelHandler} from "./TextWidget";
import {getSprintState} from "../../milestones/sprint-utils";
import {getChildCardInfo, getParentCard} from "../../workflows/workflow-utils";
import {VisionBoardQuery, VisionBoardQueryId} from "../../../cdx-models/VisionBoardQuery";
import {WithAllFieldsLoaded} from "../../../cdx-models/utils/MakeModel";
import {createJobQueue} from "@cdx/common/create-job-queue";
import {cdxRequest} from "../../../lib/request";

type FilterNode = VisionBoardNode & {type: "filter"};
type Column = (FilterNode["value"] & {mode: "table"})["columns"][number];

const colList: Column[] = [
  "priority",
  "effort",
  "owner",
  "sprint",
  "milestone",
  "checklist",
  "deck",
];

const ModeOverlay = ({
  overlayProps,
  nodeData,
  node,
  bag,
}: {
  overlayProps: any;
  nodeData: FilterNode;
  node: NodeBushEntry;
  bag: VisionBoardBag;
}) => {
  const handleChange = (val: FilterNode["value"]["mode"]) => {
    bag.store.doc.transact(() => {
      switch (val) {
        case "horBar": {
          node.node.set("value", {...nodeData.value, mode: "horBar"});
          node.node.set("pos", {...nodeData.pos, w: 200, h: 40});
          break;
        }
        case "verBar": {
          node.node.set("value", {...nodeData.value, mode: "verBar"});
          node.node.set("pos", {...nodeData.pos, w: 40, h: 200});
          break;
        }
        case "pie": {
          node.node.set("value", {...nodeData.value, mode: "pie"});
          node.node.set("pos", {...nodeData.pos, w: 100, h: 100});
          break;
        }
        case "table": {
          node.node.set("value", {...nodeData.value, mode: "table", columns: ["owner"]});
          node.node.set("pos", {...nodeData.pos, w: 160, h: 200});
          break;
        }
      }
    });
  };

  const handleColChange = (data: Record<Column, boolean>) => {
    const nextCols = Object.entries(data)
      .filter(([, v]) => v)
      .map(([k]) => k);
    node.node.set("value", {...nodeData.value, mode: "table", columns: nextCols});
  };

  return (
    <ArrowOverlay bg="white" arrowSize="sm" {...overlayProps}>
      <Col pa="12px" maxWidth="20rem">
        <RadioGroup
          onChange={handleChange}
          value={nodeData.value.mode}
          options={[
            {value: "horBar", label: "Horizontal Bar"},
            {value: "verBar", label: "Vertical Bar"},
            {value: "pie", label: "Pie Chart"},
            {
              value: "table",
              label: "Table",
              postfixBelow: nodeData.value.mode === "table" && (
                <Col sp="12px">
                  <Label>Shown Columns</Label>
                  <CheckboxGroup
                    options={colList.map((value) => ({value, label: value}))}
                    onChange={handleColChange}
                    value={Object.fromEntries(nodeData.value.columns.map((c) => [c, true]))}
                  />
                </Col>
              ),
            },
          ]}
          vertical
        />
      </Col>
    </ArrowOverlay>
  );
};

const ModeButton = (props: {nodeData: FilterNode; node: NodeBushEntry; bag: VisionBoardBag}) => {
  return (
    <DropDownForChild
      setChildProps={({isOpen}: any) => ({
        tooltip: isOpen ? null : "Switch Mode",
        active: isOpen,
      })}
      renderOverlay={(overlayProps: any) => <ModeOverlay overlayProps={overlayProps} {...props} />}
      overlayProps={{placement: "top", distanceFromAnchor: 10}}
    >
      <TooltipForChild tooltip={null}>
        <DSIconButton
          variant="secondary"
          icon={<DSIconEye />}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
        />
      </TooltipForChild>
    </DropDownForChild>
  );
};

const FilterWidgetMenuItems = ({
  nodeData,
  bag,
  node,
  labelMode,
  setLabelMode,
}: {
  nodeData: FilterNode;
  node: NodeBushEntry;
  bag: VisionBoardBag;
  labelMode: "view" | "edit";
  setLabelMode: Dispatch<"view" | "edit">;
}) => {
  const id = node.id;
  const active = bag.shownVisionBoardFilter === id;
  const closeRef = useRef(() => bag.showVisionBoardFilter(null));
  closeRef.current = () => bag.showVisionBoardFilter(null);
  useEffect(() => {
    if (!active) return;
    return () => {
      closeRef.current();
    };
  }, [active]);

  return (
    <>
      <ModeButton node={node} nodeData={nodeData} bag={bag} />
      <TooltipForChild tooltip="Edit label">
        <DSIconButton
          active={labelMode === "edit"}
          onClick={() => setLabelMode(labelMode === "edit" ? "view" : "edit")}
          variant="secondary"
          icon={<DSIconPencil />}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
        />
      </TooltipForChild>
      <TooltipForChild tooltip="Set up Filter">
        <DSIconButton
          variant="secondary"
          icon={<DSIconFlash />}
          active={active}
          onClick={() => (active ? bag.showVisionBoardFilter(null) : bag.showVisionBoardFilter(id))}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
        />
      </TooltipForChild>
    </>
  );
};

const ItemRow = ({
  virtualRow,
  cardIds,
  root,
  colSet,
}: {
  virtualRow: VirtualItem<any>;
  cardIds: CardId[];
  root: Root;
  colSet: Set<Column>;
}) => {
  const card = useInstance("card", cardIds[virtualRow.index]);
  const {result: status, isLoaded: isStatusLoaded} = checkIfPresent(
    () => (card ? (getStatusForCard(card) as "hero") : "unassigned"),
    api
  );
  const cardContainerKey = "preview";
  return (
    <Row
      style={{
        height: `${virtualRow.size - 1}px`,
        transform: `translateY(${virtualRow.start}px)`,
      }}
      bg="foreground"
      align="center"
      px="8px"
      colorTheme="white"
      className={cx(
        tableViewStyles.row.base,
        cardStatusVariants[status],
        tableViewStyles.cardRow.base
      )}
    >
      {card ? (
        <>
          <StatusRect status={status} isLoaded={isStatusLoaded} card={card} />
          {colSet.has("priority") && (
            <PrioCell card={card} canModify={false} cardContainerKey={cardContainerKey} />
          )}
          {colSet.has("effort") && (
            <EffortCell
              card={card}
              canModify={false}
              cardContainerKey={cardContainerKey}
              root={root}
            />
          )}
          {colSet.has("owner") && (
            <OwnerCell card={card} canModify={false} cardContainerKey={cardContainerKey} />
          )}
          {colSet.has("sprint") && (
            <SprintCell
              card={card}
              canModify={false}
              cardContainerKey={cardContainerKey}
              zoneInfo={null}
            />
          )}
          {colSet.has("milestone") && (
            <MsCell
              card={card}
              root={root}
              cardContainerKey={cardContainerKey}
              canModify={false}
              zoneInfo={null}
            />
          )}
          <TitleCell card={card} root={root} />
          {colSet.has("checklist") && <CheckListCell card={card} />}
          {colSet.has("deck") && (
            <DeckCell card={card} canModify={false} cardContainerKey={cardContainerKey} />
          )}
        </>
      ) : (
        <Text type="label12" color="secondary" textAlign="center" flex="auto">
          Inaccessible card
        </Text>
      )}
    </Row>
  );
};

const PAGE_SIZE = 64;

const preloadCardCellInfo = (card: Card, root: Root, colSet: Set<Column>) => {
  getStatusForCard(card);
  card.$meta.get("title");
  const p = getParentCard(card);
  if (p) {
    p.$meta.get("title");
    p.$meta.get("masterTags");
    preloadProjectTags(p.deck?.project ?? null);
  }
  getChildCardInfo(card, root.account);
  if (colSet.has("priority")) {
    card.$meta.get("priority");
  }
  if (colSet.has("effort")) {
    card.$meta.get("effort");
  }
  if (colSet.has("owner")) {
    card.$meta.get("assignee");
  }
  if (colSet.has("sprint")) {
    const sprint = card.sprint;
    if (sprint) {
      sprint.$meta.get("name");
      sprint.sprintConfig.$meta.get("color");
      getSprintState(sprint);
    }
  }
  if (colSet.has("milestone")) {
    const ms = card.milestone;
    if (ms) {
      ms.$meta.get("name");
      ms.$meta.get("date");
      ms.$meta.get("color");
    }
  }
  if (colSet.has("checklist")) {
    card.$meta.get("checkboxStats");
  }
  const deck = card.deck;
  if (deck) {
    deck.$meta.get("title");
    deck.project?.$meta.get("id");
    preloadProjectTags(deck.project);
  }
};

const createPageManager = () => {
  const loadedPages = new Set<number>();
  return {
    onLoadRange: (range: Range, cardIds: CardId[], root: Root, colSet: Set<Column>) => {
      const startPage = Math.floor(range.startIndex / PAGE_SIZE);
      const endPage = Math.floor(range.endIndex / PAGE_SIZE);
      for (let p = startPage; p <= endPage; p += 1) {
        if (loadedPages.has(p)) continue;
        const end = Math.min(cardIds.length, (p + 1) * PAGE_SIZE);
        for (let idx = p * PAGE_SIZE; idx < end; idx += 1) {
          const card = api.getModel({modelName: "card", id: cardIds[idx]});
          if (card) preloadCardCellInfo(card, root, colSet);
        }
        loadedPages.add(p);
      }
    },
  };
};

type TableProps = {
  nodeData: FilterNode & {value: {mode: "table"}};
  bag: VisionBoardBag;
  containerRef: Ref<HTMLDivElement>;
  data: Payload;
};

const InnerTable = ({
  bag,
  nodeData,
  containerRef,
  data,
}: TableProps & {data: Exclude<NonNullable<Payload>, {counts: any}>}) => {
  const {columns} = nodeData.value;
  const cardIds = data.ids;
  const scrollElementRef = useRef<HTMLDivElement | null>(null);
  const colSet = new Set(columns);
  const [pageManager] = useState(createPageManager);

  const rowVirtualizer = useVirtualizer({
    count: cardIds.length,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => CARD_ROW_HEIGHT,
    overscan: 5,
    getItemKey: (idx: number) => cardIds[idx],
    rangeExtractor: (range) => {
      if (range.startIndex === range.endIndex && range.count > 1) return [];
      pageManager.onLoadRange(range, cardIds, bag.root, colSet);
      return defaultRangeExtractor(range);
    },
  });
  return (
    <Box
      absolute
      inset="0"
      ref={scrollElementRef}
      className={makeScrollable()}
      colorTheme="gray450"
      bg="foreground"
    >
      <Box
        width="100%"
        relative
        zIndex={1}
        style={{
          minHeight: CARD_ROW_HEIGHT,
          height: `${rowVirtualizer.getTotalSize()}px`,
        }}
        rounded={4}
        colorTheme="gray25"
        ref={containerRef}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => (
          <ItemRow
            key={virtualRow.key}
            virtualRow={virtualRow}
            cardIds={cardIds}
            root={bag.root}
            colSet={colSet}
          />
        ))}
      </Box>
    </Box>
  );
};

const FullTable = ({bag, nodeData, containerRef, data}: TableProps) => {
  if (!data || "counts" in data) {
    return (
      <Col absolute inset="0" align="center" justify="center">
        <DSSpinner size={16} />
      </Col>
    );
  } else {
    return <InnerTable bag={bag} nodeData={nodeData} containerRef={containerRef} data={data} />;
  }
};

const Table = (props: TableProps) => {
  const tooFar = useSyncExternalStore(props.bag.bboxStore.subscribe, () => {
    return props.bag.posRef.current.scale < 0.5;
  });
  if (tooFar)
    return (
      <Col
        colorTheme="gray450"
        align="center"
        justify="center"
        pa="12px"
        flex="auto"
        bg="foreground"
      >
        <Text type="title24" color="secondary">
          Table View
        </Text>
      </Col>
    );
  return <FullTable {...props} />;
};
const PieChart = ({nodeData, data}: {nodeData: FilterNode; data: Payload}) => {
  if (!data || "ids" in data) {
    return (
      <Col absolute inset="0" align="center" justify="center">
        <DSSpinner size={16} />
      </Col>
    );
  }
  const {doneCardCount, undoneCardCount} = calcAggr(data);
  const sum = undoneCardCount + doneCardCount;
  const percentage = sum ? (doneCardCount / sum) * 100 : 0;
  const percentageStr = `${Math.round(percentage)}%`;

  const getType = (): null | CSSProps["textType"] => {
    const s = Math.min(nodeData.pos.w, nodeData.pos.h);
    if (s > 100) return "title26";
    if (s > 60) return "title18";
    if (s > 40) return "label14";
    return null;
  };
  const type = getType();

  return (
    <Col relative align="center" justify="center" flex="auto">
      <Box position="absolute" inset="0">
        <CircularProgressArc
          progress={percentage / 100}
          className={css(
            {width: "100%", height: "100%"},
            canvasNodeStyles.pie.type[nodeData.color ? "color" : "noColor"]
          )}
        />
      </Box>
      {type && (
        <Text type={type} color="secondary">
          {percentageStr}
        </Text>
      )}
    </Col>
  );
};
const VerBar = ({nodeData, data}: {nodeData: FilterNode; data: Payload}) => {
  if (!data || "ids" in data) {
    return (
      <Col absolute inset="0" align="center" justify="center">
        <DSSpinner size={16} />
      </Col>
    );
  }
  const {doneCardCount, undoneCardCount} = calcAggr(data);
  const sum = undoneCardCount + doneCardCount;
  const percentage = sum ? (doneCardCount / sum) * 100 : 0;
  const percentageStr = `${Math.round(percentage)}%`;

  const type = nodeData.pos.w < 40 ? "label14" : "title26";

  return (
    <>
      <Col absolute inset="0" align="center" justify="center">
        <Text type={type} color="secondary" style={{transform: "rotate(-90deg)"}}>
          {percentageStr}
        </Text>
      </Col>
      <Box
        absolute
        left="0"
        right="0"
        bottom="0"
        style={{height: `${percentage}%`}}
        bg="foreground"
        {...(nodeData.color
          ? {className: canvasNodeStyles.container.customColorBg}
          : {colorTheme: "active600"})}
      />
      <Col
        absolute
        inset="0"
        align="center"
        justify="center"
        style={{
          clipPath: `polygon(0 100%, 0 ${100 - percentage}%, 100% ${100 - percentage}%, 100% 100%)`,
        }}
        {...(nodeData.color
          ? {className: canvasNodeStyles.container.customColorBg}
          : {colorTheme: "active600"})}
      >
        <Text type={type} color="primary" style={{transform: "rotate(-90deg)"}}>
          {percentageStr}
        </Text>
      </Col>
    </>
  );
};

const calcAggr = ({counts}: Exclude<NonNullable<Payload>, {ids: any}>) => {
  const doneCardCount =
    (counts["done"] ?? 0) +
    (counts["heroDone"] ?? 0) +
    (counts["archivedDone"] ?? 0) +
    (counts["archivedHeroDone"] ?? 0);
  const undoneCardCount =
    (counts["review"] ?? 0) +
    (counts["blocked"] ?? 0) +
    (counts["assigned"] ?? 0) +
    (counts["unassigned"] ?? 0) +
    (counts["snoozing"] ?? 0) +
    (counts["started"] ?? 0) +
    (counts["hero"] ?? 0);
  return {doneCardCount, undoneCardCount};
};

const HorBar = ({nodeData, data}: {nodeData: FilterNode; data: Payload}) => {
  if (!data || "ids" in data) {
    return (
      <Col absolute inset="0" align="center" justify="center">
        <DSSpinner size={16} />
      </Col>
    );
  }
  const {doneCardCount, undoneCardCount} = calcAggr(data);

  const sum = undoneCardCount + doneCardCount;
  const percentage = sum ? (doneCardCount / sum) * 100 : 0;
  const percentageStr = `${Math.round(percentage)}%`;

  const type = nodeData.pos.h < 40 ? "label14" : "title26";

  return (
    <>
      <Col absolute inset="0" align="center" justify="center">
        <Text type={type} color="secondary">
          {percentageStr}
        </Text>
      </Col>
      <Box
        absolute
        left="0"
        top="0"
        bottom="0"
        style={{width: `${percentage}%`}}
        bg="foreground"
        {...(nodeData.color
          ? {className: canvasNodeStyles.container.customColorBg}
          : {colorTheme: "active600"})}
      />
      <Col
        absolute
        inset="0"
        align="center"
        justify="center"
        style={{clipPath: `polygon(0 0, ${percentage}% 0, ${percentage}% 100%, 0 100%)`}}
        {...(nodeData.color
          ? {className: canvasNodeStyles.container.customColorBg}
          : {colorTheme: "active600"})}
      >
        <Text type={type} color="primary">
          {percentageStr}
        </Text>
      </Col>
    </>
  );
};

const getMinDims = (mode: (VisionBoardNode & {type: "filter"})["value"]["mode"]): TwoNums => {
  switch (mode) {
    case "horBar":
      return [100, 20];
    case "verBar":
      return [20, 100];
    case "pie":
      return [20, 20];
    case "table":
      return [120, 60];
  }
};

type Mode = FilterNode["value"]["mode"];
type Payload = WithAllFieldsLoaded<VisionBoardQuery>["payload"] | null;

const reloadQueue = createJobQueue({concurrent: 2, onError: (err) => console.warn(err)});

const createSeenManager = (): {
  onSeen: (id: VisionBoardQueryId) => void;
} => {
  const lastSeen = new Map<VisionBoardQueryId, number>();
  const batch = new Set<VisionBoardQueryId>();
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const processBatch = () => {
    const ids = [...batch];
    batch.clear();
    timeoutId = null;
    void cdxRequest({path: "/services/mark-queries-as-seen", method: "POST", data: {ids}});
  };

  return {
    // mark batches of queries as seen if they haven't been seen in the last 15 minutes
    onSeen: (id: VisionBoardQueryId) => {
      const exist = lastSeen.get(id);
      if (exist && exist > Date.now() - 1000 * 60 * 15) return;
      batch.add(id);
      lastSeen.set(id, Date.now());
      if (!timeoutId) timeoutId = setTimeout(processBatch, 1000);
    },
  };
};

const seenManager = createSeenManager();

const scheduleReload = (opts: {id: VisionBoardQueryId; type: VisionBoardQuery["type"]}) => {
  return reloadQueue.add(() => api.mutate.visionBoard.reloadQuery(opts));
};

const modeToType: {[M in Mode]: VisionBoardQuery["type"]} = {
  horBar: "aggr",
  verBar: "aggr",
  pie: "aggr",
  table: "list",
};
export const useQueryData = (query: VisionBoardQuery, mode: Mode): Payload => {
  const {isLoaded, result} = checkIfPresent(() => {
    return {
      payload: query.payload as VisionBoardQueryPayload,
      correctMode: query.type === modeToType[mode],
    };
  }, api);
  const needsReload = isLoaded && (!result.correctMode || !query.payload);
  const id = query.$meta.get("id", null)!;
  const type = modeToType[mode];
  const optsRef = useRef({id, type});
  optsRef.current = {id, type};
  useEffect(() => {
    if (needsReload) return scheduleReload(optsRef.current);
  }, [needsReload]);
  return isLoaded && result.correctMode ? result.payload : null;
};

const LabelEdit = (props: {label: string; onSave: (next: string) => void}) => {
  const {label, onSave} = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const propsRef = useRef(props);
  propsRef.current = props;
  const handleBlur = () => {
    onSave(inputRef.current!.value);
  };
  useEffect(() => {
    const node = inputRef.current!;
    return () => {
      const nextLabel = node.value;
      propsRef.current.onSave(nextLabel);
    };
  }, []);
  useEsc(() => {
    onSave(inputRef.current!.value);
  });
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") onSave(inputRef.current!.value);
  };
  return (
    <Box absolute bottom="100%" left="50%" translateX="-50%" pb="8px">
      <DSInput
        size="sm"
        onKeyDown={handleKeyDown}
        defaultValue={label}
        autoFocus
        onBlur={handleBlur}
        ref={inputRef}
        className={css({textAlign: "center"})}
        onMouseDown={(e) => e.stopPropagation()}
      />
    </Box>
  );
};

const WidgetLabel = ({value, onClick}: {value: string; onClick: () => void}) => (
  <Box absolute bottom="100%" left="50%" translateX="-50%" pb="8px">
    <Text
      type="label12"
      color="primary"
      textAlign="center"
      bg="foreground"
      px="4px"
      py="2px"
      rounded={4}
      {...{onClick}}
    >
      {value}
    </Text>
  </Box>
);

const Content = ({
  nodeData,
  bag,
  query,
  containerRef,
}: {
  nodeData: FilterNode;
  bag: VisionBoardBag;
  query: VisionBoardQuery;
  containerRef: Ref<HTMLDivElement>;
}) => {
  const data = useQueryData(query, nodeData.value.mode);
  const {mode} = nodeData.value;
  switch (mode) {
    case "horBar":
      return <HorBar nodeData={nodeData} data={data} />;
    case "verBar":
      return <VerBar nodeData={nodeData} data={data} />;
    case "pie":
      return <PieChart nodeData={nodeData} data={data} />;
    case "table":
      return (
        <Table
          bag={bag}
          nodeData={nodeData as FilterNode & {value: {mode: "table"}}}
          containerRef={containerRef}
          data={data}
        />
      );
  }
};

type CardWidgetProps = {
  node: NodeBushEntry;
  nodeData: FilterNode;
  bag: VisionBoardBag;
};
const FilterWidget = (props: CardWidgetProps) => {
  const {nodeData, node, bag} = props;
  const {mode, queryId} = nodeData.value;
  const [labelMode, setLabelMode] = useState<"view" | "edit">("view");
  const {elements, nodeRef, containerProps, isActive} = useCanvasNode({
    node,
    nodeData,
    bag,
    menuItems: (
      <FilterWidgetMenuItems
        node={node}
        nodeData={nodeData}
        bag={bag}
        labelMode={labelMode}
        setLabelMode={setLabelMode}
      />
    ),
    menuOffset: labelMode === "edit" ? "48px" : nodeData.value.label ? "32px" : undefined,
    minDims: getMinDims(mode),
    forceAspect: mode === "pie" ? 1 : undefined,
    noBorder: mode === "pie",
    onDelete: () => {
      if (nodeData.value.queryId) {
        api.mutate.visionBoard.markQueryStale({id: nodeData.value.queryId});
      }
    },
  });

  const [containerRef, handleWheel] = useCanvasWheelHandler({outerRef: nodeRef});
  useEffect(() => {
    if (queryId) seenManager.onSeen(queryId);
  }, [queryId]);

  const getContent = () => {
    if (nodeData.value.filter) {
      return (
        <Text type="label11" color="secondary" pa="4px">
          Legacy widget. Please delete and create new one
        </Text>
      );
    }
    if (!queryId) {
      const handleClick: MouseEventHandler = (e) => {
        e.stopPropagation();
        bag.showVisionBoardFilter(node.id);
      };
      return (
        <Col align="center" justify="center" height="100%">
          <DSButton variant="tertiary" onClick={handleClick} size="sm">
            Set up filter
          </DSButton>
        </Col>
      );
    } else {
      const query = api.getModel({modelName: "visionBoardQuery", id: queryId});
      if (!query) {
        return (
          <Text type="label11" color="secondary" pa="4px">
            Invalid query. Please update.
          </Text>
        );
      }

      return <Content bag={bag} nodeData={nodeData} query={query} containerRef={containerRef} />;
    }
  };

  const handleLabelSave = (nextLabel: string) => {
    if (nextLabel !== nodeData.value.label) {
      node.node.set("value", {...nodeData.value, label: nextLabel});
    }
    setLabelMode("view");
  };

  useEffect(() => {
    if (!isActive) setLabelMode("view");
  }, [isActive]);

  const handleLabelClick = () => {
    if (isActive) setLabelMode("edit");
  };

  return (
    <div
      {...containerProps}
      ref={nodeRef}
      className={css(
        {
          flex: "none",
          display: "flex",
          rounded: 4,
          colorTheme: "gray550",
          bg: "foreground",
        },
        containerProps.className
      )}
      onWheelCapture={handleWheel}
    >
      {getContent()}
      {elements}
      {labelMode === "edit" ? (
        <LabelEdit label={nodeData.value.label ?? ""} onSave={handleLabelSave} />
      ) : nodeData.value.label ? (
        <WidgetLabel value={nodeData.value.label} onClick={handleLabelClick} />
      ) : null}
    </div>
  );
};

export default FilterWidget;
